import tenantUtils from '@utils';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/ar';
import { getClassifiedBaseURL } from '../../../../utility/env';
import { getLocaleForURL } from '../../../../utility/language';
import { DAY_MONTH_YEAR_FORMAT, YEAR_MONTH_DATE_FORMAT } from '../../../../constants/formats';

dayjs.extend(relativeTime);

const isEmptyObject = (value) => !value || (typeof value === 'object' && Object.keys(value).length === 0);

// Feature gate — arrives on state.app.loginUser.user from /api/surge/users/current.
export const isAdLicenseVisibilityEnabled = (user) => user?.is_ad_license_visibility_enabled === true;

// Dashboard / backlog card state, driven by the summary endpoint.
export const getCardState = (summary) => {
  if (!summary?.fal_verified) return 'unverified';
  if (summary?.unposted_count === 0) return 'clear';
  return 'pending';
};

// Assistance entry-point state.
export const getAssistanceState = (assistance) => {
  if (assistance?.available && !assistance?.requested) return 'cta';
  if (assistance?.available && assistance?.requested) return 'requested';
  return 'none';
};

// Only agency admins rescoping to a specific agent send q[user_id_eq] to summary.
// Never send it for a non-admin — that would hide the unassigned pool from an agent.
// id === -1 is the "(Agency)" option, which means "no filter".
export const getSummaryUserId = (loginUser, selectedUser) =>
  loginUser?.is_agency_admin && selectedUser?.id && selectedUser.id !== -1 ? selectedUser.id : undefined;

// Same URL profile-completion-list.js builds for FAL (REGA) verification.
export const getVerifyFalUrl = () =>
  `${getClassifiedBaseURL()}${getLocaleForURL()}/verification/rega/?redirectPath=${encodeURIComponent(
    window.location.href,
  )}`;

// GA4 events are dispatched on a 10ms timeout (see gtmEventHandler), so a synchronous
// window.location redirect tears the page down before the beacon is even queued and the
// click event is lost. Fire the GA4 event first, then call this to navigate after a short
// delay that lets the event flush (and stay visible in the console/network tab).
export const redirectToVerifyFal = () => {
  setTimeout(() => {
    window.location.href = getVerifyFalUrl();
  }, 250);
};

// Localised "{Property Type} For Sale|For Rent". '' when property_type is missing/{}.
export const getLicenseTitle = (propertyType, purpose, t) => {
  if (isEmptyObject(propertyType)) return '';
  const typeTitle = tenantUtils.getLocalisedString(propertyType, 'title') || '';
  const purposeSlug = purpose?.slug || '';
  const purposeLabel = /rent/i.test(purposeSlug) ? t('For Rent') : t('For Sale');
  return [typeTitle, purposeLabel].filter(Boolean).join(' ').trim();
};

// "{District}, {City}" — district = localised location title, city = breadcrumb level 2.
// '' when location is missing/{}.
export const getLicenseLocation = (location) => {
  if (isEmptyObject(location)) return '';
  const district = tenantUtils.getLocalisedString(location, 'title') || '';
  const cityCrumb = (location?.breadcrumbs || []).find((crumb) => crumb?.level === 2);
  const city = cityCrumb ? tenantUtils.getLocalisedString(cityCrumb, 'title') || '' : '';
  return [district, city].filter(Boolean).join(', ');
};

export const formatIssuedAgo = (date, locale) => {
  if (!date) return '';
  return dayjs(date)
    .locale(locale === 'ar' ? 'ar' : 'en')
    .fromNow();
};

export const formatIssuedDate = (date) => (date ? dayjs(date).format(DAY_MONTH_YEAR_FORMAT) : '');

export const formatDetailDate = (date) => (date ? dayjs(date).format(YEAR_MONTH_DATE_FORMAT) : '');

// Individual sellers have no agency — the agent column/filter is suppressed for them.
export const isIndividualSeller = (user) => !user?.agency;
